import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import { I } from './Icons'

const Section = styled.section`
  margin-top: 28px;
`

const Card = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowSm};
  padding: 0;
  overflow: hidden;
`

const ApHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: ${p => (p.$collapsed ? 'none' : `1px solid ${p.theme.colors.semantic.border}`)};
  cursor: pointer;
`

const HeadTitle = styled.span`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`

const Chips = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
  align-items: center;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => p.$dot};
  }
`

const ApList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`

const ApItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  border-top: 1px solid ${p => p.theme.colors.semantic.border};
  cursor: pointer;
  transition: background 0.12s;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceRaised};
  }
  &:nth-child(odd) {
    border-right: 1px solid ${p => p.theme.colors.semantic.border};
  }
`

const Sev = styled.div`
  width: 6px;
  border-radius: 3px;
  flex-shrink: 0;
  margin: 2px 0;
  background: ${p => p.$c};
`

const Txt = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
`

const ItemDetail = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.textMuted};
  line-height: 1.45;
`

const Caret = styled.div`
  color: ${p => p.theme.colors.semantic.textFaint};
  flex-shrink: 0;
  align-self: center;
  transform: ${p => (p.$open ? 'rotate(0)' : 'rotate(-90deg)')};
  transition: transform 0.15s;
`

function sevColor(theme, f) {
  if (f.severity === 'critical') return theme.colors.semantic.danger
  if (f.severity === 'warn') return theme.colors.semantic.warn
  return theme.colors.semantic.accent
}

const WipAiActionPoints = ({ findings, onAnchor, show }) => {
  const theme = useTheme()
  const [open, setOpen] = useState(true)
  if (!show) return null

  const counts = findings.reduce((a, f) => {
    a[f.severity] = (a[f.severity] || 0) + 1
    return a
  }, {})

  const s = theme.colors.semantic

  return (
    <Section id="action-points">
      <Card>
        <ApHead $collapsed={!open} onClick={() => setOpen(o => !o)}>
          <HeadTitle>Action Points</HeadTitle>
          <Chips>
            {counts.critical ? (
              <Chip $bg={s.dangerTint} $fg={s.danger} $dot={s.danger}>
                <span className="dot" />
                {counts.critical} critical
              </Chip>
            ) : null}
            {counts.warn ? (
              <Chip $bg={s.warnTint} $fg={s.warn} $dot={s.warn}>
                <span className="dot" />
                {counts.warn} warnings
              </Chip>
            ) : null}
            {counts.info ? (
              <Chip $bg={s.lilacTint} $fg={s.accent} $dot={s.accent}>
                <span className="dot" />
                {counts.info} info
              </Chip>
            ) : null}
            {findings.length === 0 ? (
              <Chip $bg={s.successTint} $fg={s.success} $dot={s.success}>
                <span className="dot" />
                All clear
              </Chip>
            ) : null}
          </Chips>
          <Caret $open={open}>
            <I.chev width={16} height={16} />
          </Caret>
        </ApHead>
        {findings.length > 0 && open && (
          <ApList>
            {findings.map(f => (
              <ApItem key={f.id} onClick={() => onAnchor(f.anchor)}>
                <Sev $c={sevColor(theme, f)} />
                <Txt>
                  <ItemTitle>{f.title}</ItemTitle>
                  <ItemDetail>{f.detail}</ItemDetail>
                </Txt>
                <I.arrowRight style={{ width: 14, height: 14 }} />
              </ApItem>
            ))}
          </ApList>
        )}
      </Card>
    </Section>
  )
}

WipAiActionPoints.propTypes = {
  findings: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAnchor: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
}

export default WipAiActionPoints
